import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import api from "@/lib/api";

function Section({ title, children, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-borderc p-6 space-y-3">
      <div>
        <h2 className="font-semibold text-brand-navy">{title}</h2>
        {desc && <p className="text-xs text-brand-muted mt-0.5">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

export default function AdminSettings() {
  const [s, setS] = useState({
    razorpay_key_id: "", razorpay_key_secret: "", razorpay_enabled: true,
    phonepe_merchant_id: "", phonepe_api_key: "", phonepe_enabled: true, test_mode: true,
  });
  const [m, setM] = useState({
    enabled: false, upi_id: "", upi_display_name: "", upi_qr_url: "",
    bank_account_name: "", bank_name: "", bank_account_number: "", bank_ifsc: "", bank_branch: "",
    instructions: "", support_email: "", support_whatsapp: "",
  });

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS((s0) => ({ ...s0, ...r.data })));
    api.get("/admin/manual-payments/settings").then((r) => setM((m0) => ({ ...m0, ...r.data })));
  }, []);

  const saveAuto = async () => { await api.put("/admin/settings", s); toast.success("Payment gateway settings saved"); };
  const saveManual = async () => { await api.put("/admin/manual-payments/settings", m); toast.success("Manual payment settings saved"); };

  const onQrFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("QR image too large (max 1.5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setM({ ...m, upi_qr_url: reader.result });
    reader.readAsDataURL(f);
  };

  return (
    <div className="px-5 lg:px-10 py-8 max-w-3xl mx-auto" data-testid="admin-settings">
      <h1 className="text-3xl font-bold text-brand-navy">Payment settings</h1>
      <p className="text-sm text-brand-muted mt-1">Configure automated gateways (Razorpay/PhonePe) and the optional Manual UPI/Bank Transfer fallback.</p>

      <div className="mt-6 space-y-5">
        <Section title="Manual UPI / Bank Transfer" desc="Optional fallback for users who don't want to use a payment gateway. Plans activate only after admin verification.">
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="manual-enable" type="checkbox" checked={!!m.enabled} onChange={(e) => setM({ ...m, enabled: e.target.checked })} /> Enable manual payments on Billing page
          </label>

          <div className="pt-2">
            <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">UPI</div>
            <div className="mt-2 grid md:grid-cols-2 gap-3">
              <input data-testid="upi-id" placeholder="UPI ID (e.g. draftvoice@hdfc)" value={m.upi_id || ""} onChange={(e) => setM({ ...m, upi_id: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
              <input placeholder="UPI display name (e.g. DraftVoice AI)" value={m.upi_display_name || ""} onChange={(e) => setM({ ...m, upi_display_name: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
            </div>
            <div className="mt-3 flex items-start gap-3">
              <label className="rounded-xl border border-dashed border-brand-borderc px-4 py-3 text-sm text-brand-text cursor-pointer inline-flex items-center gap-2 hover:bg-brand-surface">
                <Upload className="w-4 h-4" /> Upload UPI QR
                <input data-testid="upi-qr-upload" type="file" accept="image/*" className="hidden" onChange={onQrFile} />
              </label>
              {m.upi_qr_url && (
                <div className="flex items-center gap-2">
                  <img src={m.upi_qr_url} alt="QR" className="w-20 h-20 rounded-xl border border-brand-borderc bg-white object-contain" />
                  <button onClick={() => setM({ ...m, upi_qr_url: "" })} className="text-red-500 text-xs inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Remove</button>
                </div>
              )}
            </div>
          </div>

          <div className="pt-2">
            <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">Bank account (NEFT/IMPS)</div>
            <div className="mt-2 grid md:grid-cols-2 gap-3">
              <input placeholder="Account holder name" value={m.bank_account_name || ""} onChange={(e) => setM({ ...m, bank_account_name: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
              <input placeholder="Bank name" value={m.bank_name || ""} onChange={(e) => setM({ ...m, bank_name: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
              <input data-testid="bank-acct" placeholder="Account number" value={m.bank_account_number || ""} onChange={(e) => setM({ ...m, bank_account_number: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
              <input data-testid="bank-ifsc" placeholder="IFSC" value={m.bank_ifsc || ""} onChange={(e) => setM({ ...m, bank_ifsc: e.target.value.toUpperCase() })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
              <input placeholder="Branch (optional)" value={m.bank_branch || ""} onChange={(e) => setM({ ...m, bank_branch: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm md:col-span-2" />
            </div>
          </div>

          <div className="pt-2">
            <label className="text-xs text-brand-muted">Payment instructions (shown to user)</label>
            <textarea value={m.instructions || ""} onChange={(e) => setM({ ...m, instructions: e.target.value })} rows={3} className="w-full px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" placeholder="e.g. Please mention your registered email in the payment remarks…" />
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <input placeholder="Support email" value={m.support_email || ""} onChange={(e) => setM({ ...m, support_email: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
            <input placeholder="Support WhatsApp (e.g. +91 90000 00000)" value={m.support_whatsapp || ""} onChange={(e) => setM({ ...m, support_whatsapp: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
          </div>

          <button data-testid="save-manual" onClick={saveManual} className="btn-primary-gradient rounded-full px-6 py-2.5 text-sm font-semibold">Save manual payment settings</button>
        </Section>

        <Section title="Razorpay (automated)" desc="Add live keys to accept cards, UPI Intent, and netbanking via Razorpay. Until live keys are added, this gateway stays hidden from users.">
          <div className="grid md:grid-cols-2 gap-3">
            <input placeholder="Key ID (rzp_live_… or rzp_test_…)" value={s.razorpay_key_id || ""} onChange={(e) => setS({ ...s, razorpay_key_id: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
            <input placeholder="Key Secret" value={s.razorpay_key_secret || ""} onChange={(e) => setS({ ...s, razorpay_key_secret: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.razorpay_enabled} onChange={(e) => setS({ ...s, razorpay_enabled: e.target.checked })} /> Enable Razorpay
          </label>
        </Section>

        <Section title="PhonePe (automated)">
          <div className="grid md:grid-cols-2 gap-3">
            <input placeholder="Merchant ID" value={s.phonepe_merchant_id || ""} onChange={(e) => setS({ ...s, phonepe_merchant_id: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
            <input placeholder="API Key" value={s.phonepe_api_key || ""} onChange={(e) => setS({ ...s, phonepe_api_key: e.target.value })} className="px-3 py-2.5 rounded-xl border border-brand-borderc text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.phonepe_enabled} onChange={(e) => setS({ ...s, phonepe_enabled: e.target.checked })} /> Enable PhonePe
          </label>
        </Section>

        <Section title="Mode">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={!!s.test_mode} onChange={(e) => setS({ ...s, test_mode: e.target.checked })} /> Test mode (automated gateways stay hidden from users until you switch this off and provide live keys)
          </label>
        </Section>

        <button data-testid="save-auto" onClick={saveAuto} className="btn-primary-gradient rounded-full px-6 py-3 text-sm font-semibold">Save automated gateway settings</button>
      </div>
    </div>
  );
}
